/**
 * The environment panel: time of day, weather, wind.
 *
 * CLAUDE.md schedules the real settings menu for M4 and says a "temporary debug UI is fine" for
 * this milestone's environment controls. This is that — plain DOM, no framework, matching the
 * existing overlays — but it is deliberately built around the *right* controls rather than a
 * pile of sliders, because which knobs exist is a design decision that M4 will inherit whatever
 * this milestone does.
 *
 * The one that matters is the time control. It is a slider over **local solar hours**, not UTC
 * and not a time zone: noon on the slider puts the sun on the meridian wherever the aircraft
 * happens to be (see `render/environment.ts`). Next to it are the named presets, which are not
 * fixed clock times either — each one solves for a sun *elevation*, so "dusk" means the same
 * light at Innsbruck in January as at San Francisco in July even though the two are hours apart
 * on any clock. That distinction is the difference between a time control that is useful for
 * setting up a shot and one that needs a mental time-zone conversion first.
 *
 * `updateFrame(alpha)`: yes — {@link EnvironmentPanel.updateFrame} refreshes the readouts, and
 * only when a value has actually changed, because writing to `textContent` every frame for a
 * dozen elements is a layout thrash the profiler notices. Nothing here allocates per frame.
 */

import { TIME_PRESETS, type Environment, type TimePresetName } from '../render/environment.js';
import { WEATHER_PRESETS, type WeatherPresetName } from '../world/weather.js';

/** Time-lapse rates offered, in simulated seconds per real second. */
const TIME_SCALES = [
  { label: 'frozen', scale: 0 },
  { label: '60×', scale: 60 },
  { label: '600×', scale: 600 },
  { label: '3600×', scale: 3_600 },
] as const;

export interface EnvironmentPanelOptions {
  environment: Environment;
  /** Called after any change, so the scene can re-publish immediately even while paused. */
  onChange: () => void;
  /** Called when the weather changes, so the wind model can be rebuilt. */
  onWeatherChange: () => void;
}

export class EnvironmentPanel {
  private readonly root: HTMLElement;
  private readonly environment: Environment;
  private readonly onChange: () => void;
  private readonly onWeatherChange: () => void;

  private readonly timeSlider: HTMLInputElement;
  private readonly timeReadout: HTMLElement;
  private readonly sunReadout: HTMLElement;
  private readonly moonReadout: HTMLElement;
  private readonly windSpeed: HTMLInputElement;
  private readonly windDirection: HTMLInputElement;
  private readonly windReadout: HTMLElement;
  private readonly exposureSlider: HTMLInputElement;
  private readonly exposureReadout: HTMLElement;
  private readonly weatherButtons = new Map<WeatherPresetName, HTMLButtonElement>();
  private readonly scaleButtons = new Map<number, HTMLButtonElement>();

  /** Last rendered readouts, so the DOM is only touched when something moved. */
  private lastTimeText = '';
  private lastSunText = '';
  /** Last values shown, as numbers, so the guard can run before the string is built. */
  private lastMoonLit = Number.NaN;
  private lastMoonElevation = Number.NaN;
  private nextUpdateMs = 0;

  constructor(options: EnvironmentPanelOptions) {
    this.environment = options.environment;
    this.onChange = options.onChange;
    this.onWeatherChange = options.onWeatherChange;

    this.root = document.createElement('section');
    this.root.className = 'envpanel';
    this.root.hidden = true;

    const title = document.createElement('div');
    title.className = 'envpanel__title';
    title.textContent = 'ENVIRONMENT';
    this.root.append(title);

    // --- time ---------------------------------------------------------------------------------
    this.timeReadout = this.addRow('local solar', '—');
    this.timeSlider = document.createElement('input');
    this.timeSlider.type = 'range';
    this.timeSlider.min = '0';
    this.timeSlider.max = '24';
    // Two-minute resolution: fine enough to place the sun exactly on a ridge line, coarse
    // enough that dragging the full day is a single sweep rather than a thousand steps.
    this.timeSlider.step = '0.0333';
    this.timeSlider.className = 'envpanel__slider';
    this.timeSlider.addEventListener('input', () => {
      this.environment.setLocalSolarHours(Number.parseFloat(this.timeSlider.value));
      this.onChange();
    });
    this.root.append(this.timeSlider);

    this.root.append(
      this.buildButtonRow(TIME_PRESETS, (preset) => {
        this.environment.setTimePreset(preset as TimePresetName);
        this.syncFromEnvironment();
        this.onChange();
      }),
    );

    this.sunReadout = this.addRow('sun', '—');
    this.moonReadout = this.addRow('moon', '—');

    // --- time lapse ---------------------------------------------------------------------------
    const scaleRow = document.createElement('div');
    scaleRow.className = 'envpanel__buttons';
    for (const { label, scale } of TIME_SCALES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'envpanel__button';
      button.textContent = label;
      button.addEventListener('click', () => {
        this.environment.setTimeScale(scale);
        this.syncScaleButtons();
      });
      this.scaleButtons.set(scale, button);
      scaleRow.append(button);
    }
    this.root.append(this.label('time lapse'), scaleRow);

    // --- weather ------------------------------------------------------------------------------
    const weatherRow = document.createElement('div');
    weatherRow.className = 'envpanel__buttons';
    for (const preset of WEATHER_PRESETS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'envpanel__button';
      button.textContent = preset;
      button.addEventListener('click', () => {
        this.environment.setWeatherPreset(preset);
        this.syncFromEnvironment();
        this.onWeatherChange();
        this.onChange();
      });
      this.weatherButtons.set(preset, button);
      weatherRow.append(button);
    }
    this.root.append(this.label('sky condition'), weatherRow);

    // --- wind ---------------------------------------------------------------------------------
    this.windReadout = this.addRow('wind', '—');
    this.windDirection = this.addSlider(0, 359, 1, () => this.pushWind());
    this.windSpeed = this.addSlider(0, 25, 0.5, () => this.pushWind());

    // --- exposure -----------------------------------------------------------------------------
    this.exposureReadout = this.addRow('exposure trim', '—');
    this.exposureSlider = this.addSlider(-2, 2, 0.1, () => {
      // A trim in *stops*, because that is the unit anyone who has adjusted an exposure thinks
      // in — one step is a doubling, which a linear multiplier slider hides.
      const stops = Number.parseFloat(this.exposureSlider.value);
      this.environment.setExposureBias(Math.pow(2, stops));
      this.onChange();
    });

    const hint = document.createElement('div');
    hint.className = 'envpanel__hint';
    hint.textContent = 'T to close';
    this.root.append(hint);

    document.body.append(this.root);
    this.syncFromEnvironment();
  }

  private label(text: string): HTMLElement {
    const element = document.createElement('div');
    element.className = 'envpanel__label';
    element.textContent = text;
    return element;
  }

  private addRow(name: string, value: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'envpanel__row';
    const label = document.createElement('span');
    label.className = 'envpanel__rowlabel';
    label.textContent = name;
    const readout = document.createElement('span');
    readout.className = 'envpanel__rowvalue';
    readout.textContent = value;
    row.append(label, readout);
    this.root.append(row);
    return readout;
  }

  private addSlider(min: number, max: number, step: number, onInput: () => void): HTMLInputElement {
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.className = 'envpanel__slider';
    slider.addEventListener('input', onInput);
    this.root.append(slider);
    return slider;
  }

  private buildButtonRow(
    names: readonly string[],
    onClick: (name: string) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'envpanel__buttons';
    for (const name of names) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'envpanel__button';
      button.textContent = name;
      button.addEventListener('click', () => onClick(name));
      row.append(button);
    }
    return row;
  }

  private pushWind(): void {
    this.environment.patchWeather({
      windDirectionDeg: Number.parseFloat(this.windDirection.value),
      windSpeedMps: Number.parseFloat(this.windSpeed.value),
    });
    this.onWeatherChange();
    this.onChange();
  }

  /** Pull every control back into agreement with the environment. */
  private syncFromEnvironment(): void {
    const weather = this.environment.weather;
    this.timeSlider.value = String(this.environment.localSolarHours);
    this.windDirection.value = String(Math.round(weather.windDirectionDeg));
    this.windSpeed.value = String(weather.windSpeedMps);
    this.exposureSlider.value = '0';

    for (const [preset, button] of this.weatherButtons) {
      button.classList.toggle('envpanel__button--active', preset === weather.preset);
    }
    this.syncScaleButtons();
  }

  private syncScaleButtons(): void {
    for (const [scale, button] of this.scaleButtons) {
      button.classList.toggle('envpanel__button--active', scale === this.environment.timeScale);
    }
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (visible) this.syncFromEnvironment();
  }

  toggle(): void {
    this.setVisible(this.root.hidden);
  }

  /**
   * Refresh the readouts.
   *
   * Throttled to five updates a second and guarded against writing unchanged strings. Both
   * matter: this panel has a dozen text nodes, and rewriting them every frame is a style
   * recalculation and a layout on every frame, which shows up in the render-CPU channel as a
   * cost that has nothing to do with rendering.
   */
  updateFrame(nowMs: number): void {
    if (this.root.hidden || nowMs < this.nextUpdateMs) return;
    this.nextUpdateMs = nowMs + 200;

    const hours = this.environment.localSolarHours;
    const whole = Math.floor(hours);
    const minutes = Math.round((hours - whole) * 60);
    const timeText = `${String(whole).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    if (timeText !== this.lastTimeText) {
      this.lastTimeText = timeText;
      this.timeReadout.textContent = timeText;
      // While the clock is running the slider has to follow it, or the control and the world
      // disagree the moment anyone touches it.
      if (this.environment.timeScale > 0) this.timeSlider.value = String(hours);
    }

    const sunText = `${this.environment.sunElevationDeg.toFixed(1)}° el · ${this.environment.sunAzimuthDeg.toFixed(0)}° az`;
    if (sunText !== this.lastSunText) {
      this.lastSunText = sunText;
      this.sunReadout.textContent = sunText;
    }

    const weather = this.environment.weather;
    this.windReadout.textContent =
      `${String(Math.round(weather.windDirectionDeg)).padStart(3, '0')}° / ` +
      `${(weather.windSpeedMps * 1.943_84).toFixed(0)} kt`;

    const bias = Math.log2(this.environment.exposure / Math.max(1e-6, this.environment.exposure));
    this.exposureReadout.textContent = `${this.environment.exposure.toFixed(2)}× (${bias >= 0 ? '+' : ''}${Number.parseFloat(this.exposureSlider.value).toFixed(1)} EV)`;
  }

  /**
   * The moon readout needs the night sky, which the panel does not own.
   *
   * Compared as *numbers* before the string is built, not after. The obvious spelling — build the
   * text, compare it against the last one, return if it matches — has a guard that does nothing for
   * the thing it was written to avoid: this runs every frame, the readout changes about once a
   * minute, and the early-out already skipped the DOM write. What it did not skip was two
   * `toFixed` calls and a template literal, which are the allocation. Rounding first and comparing
   * the rounded integers moves the guard in front of the work.
   */
  setMoon(illuminatedFraction: number, elevationDeg: number): void {
    const litPercent = Math.round(illuminatedFraction * 100);
    const elevation = Math.round(elevationDeg);
    if (litPercent === this.lastMoonLit && elevation === this.lastMoonElevation) return;
    this.lastMoonLit = litPercent;
    this.lastMoonElevation = elevation;
    this.moonReadout.textContent = `${litPercent}% lit · ${elevation}° el`;
  }

  dispose(): void {
    this.root.remove();
  }
}
